import { memo, useEffect, useRef, useState } from 'react';
import { addMonths, format, startOfDay, startOfMonth, subMonths } from 'date-fns';
import {
  Button,
  CarouselSelector,
  Menu,
  MonthPicker,
  Stack,
  Box,
  YearPicker,
} from '@bymason-private/legacy-pattern-components';

const MIN_DATE = getMonthDate(new Date(2020, 3, 1));

export interface MonthYearSelectorProps {
  onMonthChange: (newMonth: Date) => void;
}

export default memo(function MonthYearSelector({ onMonthChange }: MonthYearSelectorProps) {
  const monthButton = useRef(null);
  const [monthDate, setMonthDate] = useState(getMonthDate());
  const [pickedDate, setPickedDate] = useState<Date | null>(getMonthDate());
  const [openMonthYear, setOpenMonthYear] = useState(false);

  const disableNext = getMonthDate() <= monthDate;
  const disablePrev = monthDate <= MIN_DATE;

  const handleNextClick = () => {
    const newDate = addMonths(monthDate, 1);

    setMonthDate(newDate);
    setPickedDate(newDate);
  };

  const handlePrevClick = () => {
    const newDate = subMonths(monthDate, 1);

    setMonthDate(newDate);
    setPickedDate(newDate);
  };

  const handleMonthYearClick = () => {
    setOpenMonthYear(true);
  };

  const handleMonthYearClose = () => {
    setOpenMonthYear(false);
  };

  const handleMonthYearPick = (date: Date | null) => {
    // don't allow date picked to go outside min/max limits when changing year
    if (date && date < MIN_DATE) {
      setPickedDate(MIN_DATE);
    } else if (date && date > getMonthDate()) {
      setPickedDate(getMonthDate());
    } else {
      setPickedDate(date);
    }
  };

  const handleMonthYearOk = () => {
    if (pickedDate) {
      const pickedMonthDate = getMonthDate(pickedDate);
      setMonthDate(pickedMonthDate);
    }

    handleMonthYearClose();
  };

  useEffect(() => {
    onMonthChange(monthDate);
  }, [monthDate, onMonthChange]);

  return (
    <CarouselSelector
      disableNext={disableNext}
      disablePrevious={disablePrev}
      label={
        <>
          <Button
            sx={{
              flexGrow: 1,
              marginRight: 2,
              minWidth: 136,
              textAlign: 'right',
              fontSize: '1.1em',
              fontWeight: 'normal',
            }}
            disableElevation
            onClick={handleMonthYearClick}
            ref={monthButton}
            variant='outlined'
          >
            {format(monthDate, 'MMMM, Y')}
          </Button>
          <Menu
            anchorEl={monthButton.current}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            onClose={handleMonthYearClose}
            open={openMonthYear}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Stack alignItems='center' direction='column' spacing={1.5}>
              <YearPicker
                date={pickedDate}
                maxDate={getMonthDate()}
                minDate={MIN_DATE}
                onChange={handleMonthYearPick}
              />
              <MonthPicker
                date={pickedDate}
                maxDate={getMonthDate()}
                minDate={MIN_DATE}
                onChange={handleMonthYearPick}
              />
              <Box
                display='flex'
                justifyContent='flex-end'
                pb={1.5}
                pt={1}
                px={1}
                width='100%'
                columnGap={2}
                bgcolor='background.paper'
              >
                <Button color='secondary' onClick={handleMonthYearClose} variant='text'>
                  Cancel
                </Button>
                <Button onClick={handleMonthYearOk} variant='contained'>
                  Set month and year
                </Button>
              </Box>
            </Stack>
          </Menu>
        </>
      }
      onNextClick={handleNextClick}
      onPreviousClick={handlePrevClick}
    />
  );
});

//
//
// helpers
//
//

function getMonthDate(date = new Date()) {
  return startOfMonth(startOfDay(date));
}
